import { useState, type FormEvent, type ReactElement } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Notice } from '@/components/admin/Notice';
import { resetPassword } from '@/features/auth/api';
import { ApiError } from '@/lib/api/http';

const MIN_PASSWORD_LENGTH = 10;

/**
 * 再設定メールのリンクから開く、新しいパスワードの設定画面（D-054）。
 * トークンはクエリ文字列（?token=...）で受け取る。
 */
export function PasswordResetConfirmPage(): ReactElement {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError('新しいパスワードが一致しません。');
      return;
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`パスワードは${MIN_PASSWORD_LENGTH}文字以上にしてください。`);
      return;
    }

    setBusy(true);
    try {
      await resetPassword(token, newPassword);
      setDone(true);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'パスワードを設定できませんでした。もう一度お試しください。',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-wpcanvas px-4">
      <div className="w-full max-w-sm">
        <h1 className="mb-1 text-center text-xl font-semibold text-wptext-strong">CXO GROWTH</h1>
        <p className="mb-6 text-center text-wptext-muted">新しいパスワードの設定</p>

        <div className="rounded-sm border border-wpborder bg-white px-5 py-6 shadow-sm">
          {error && (
            <Notice kind="error" dismissible={false}>
              {error}
            </Notice>
          )}

          {!token ? (
            <p className="text-wptext">
              再設定リンクが正しくありません。メールのリンクをもう一度開いてください。
            </p>
          ) : done ? (
            <>
              <Notice kind="success" dismissible={false}>
                パスワードを設定しました。
              </Notice>
              <p className="mt-3 text-wptext">
                新しいパスワードでログインしてください。他の端末でログインしたままの状態は解除されています。
              </p>
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="mt-4 w-full rounded-sm bg-wpaccent px-3 py-2 text-white hover:bg-wpaccent-hover"
              >
                ログイン画面へ
              </button>
            </>
          ) : (
            <form onSubmit={(event) => void onSubmit(event)}>
              <label htmlFor="reset-new-password" className="mb-1.5 block text-wptext-muted">
                新しいパスワード（{MIN_PASSWORD_LENGTH}文字以上）
              </label>
              <input
                id="reset-new-password"
                type="password"
                autoComplete="new-password"
                required
                minLength={MIN_PASSWORD_LENGTH}
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                className="mb-4 w-full rounded-sm border border-wpborder px-3 py-2"
              />

              <label htmlFor="reset-confirm-password" className="mb-1.5 block text-wptext-muted">
                新しいパスワード（確認のためもう一度）
              </label>
              <input
                id="reset-confirm-password"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                className="mb-5 w-full rounded-sm border border-wpborder px-3 py-2"
              />

              <button
                type="submit"
                disabled={busy}
                className="w-full rounded-sm bg-wpaccent px-3 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
              >
                {busy ? '設定中…' : 'パスワードを設定する'}
              </button>
            </form>
          )}
        </div>

        {!done && (
          <p className="mt-4 text-center text-xs text-wptext-muted">
            リンクの有効期限が切れた場合は、
            <Link to="/password/reset-request" className="text-wpaccent hover:underline">
              もう一度メールを送る
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
